import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { memo, useMemo } from 'react';
import strings from '../../../Globalization';
import Beaker from '../../../components/common/Beaker';
import DigitalGraph from '../../../components/common/DigitalGraph';
import ThermometerChart from '../../../components/common/Thermometer';
import WaterIndicatorChart from '../../../components/common/WaterIndicatorChart';
import GaugeChart from '../../../components/common/charts/GaugeChart';
import consts from '../../../consts';
import { useCycleInfo } from '../../../contexts/CycleInfo/Context';

const Wash = ({ property="WASH", propertyName }) => {
	const { data: cycle } = useCycleInfo();
    const data = cycle.ChartDetail[property]
	const isDescalingCycle = cycle.CycleName.match(/\bDESCALING CYCLE\b/i);
	const theme = useTheme();
	const waterLvlReached = Array.isArray(data?.Wash?.WaterLevel?.WaterLvlReached)
		? data?.Wash?.WaterLevel?.WaterLvlReached[0]
		: data?.Wash?.WaterLevel?.WaterLvlReached;
	const waterLvlReachedValue = waterLvlReached ? waterLvlReached : '';
	const isEnspire = cycle.EquipmentFamilyId === consts.EquipmentFamilyType.ENSPIRE;

	const gaugeChartProps = useMemo(() => {
		const chartProps = {
			min: 0,
			max: parseFloat(isEnspire ? consts.GaugeChart.MaxScale : data?.Wash?.Pressure?.MaxLimit),
			segments: [],
			value: parseFloat(data?.Wash?.Pressure?.MinPressRec)
		};
		const upperLimit =
			data.Wash.Pressure.MinPressRec > data.Wash.Pressure.MinReqPress
				? data.Wash.Pressure.MinPressRec + 10
				: data.Wash.Pressure.MinReqPress + 10;
		const middleLimit = data.Wash.Pressure.MinReqPress;
		const min1 = data.Wash.Pressure.MinLimit;
		const max1 = data.Wash.Pressure.MinReqPress;
		const min2 = max1;
		const max2 = Number(isEnspire ? consts.GaugeChart.MidScale : data?.Wash?.Pressure?.MiddleLimit);
		const min3 = max2;
		const max3 = isEnspire ? consts.GaugeChart.MaxScale : (upperLimit + (upperLimit - middleLimit));
		chartProps.max = max3;
		chartProps.min = min1;
		chartProps.segments.push({
			min: min1,
			max: parseFloat(data?.Wash?.Pressure?.MinReqPress),
			color: theme.palette.error.light
		});

		chartProps.segments.push({
			min: min2,
			max: max2,
			color: theme.palette.success.light
		});

		chartProps.segments.push({
			min: min3,
			max: max3,
			color: theme.palette.error.light
		});

		return chartProps;
	}, [isEnspire, data]);

	

	return (
		<Grid container>
			<Grid size={12}>
				<Card>
					<CardContent sx={{ paddingTop: 4, paddingBottom: 4 }}>
						<Grid container>
							<Grid item size={12} display={'flex'} flexDirection={'column'} gap={2}>
								<Box
									display={'flex'}
									sx={{
										borderTop: (theme) => `1px solid ${theme.palette.divider}`,
										borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
										padding: (theme) => theme.spacing(1)
									}}
								>
									<Typography variant="subtitle1">
										{data.Wash.ThermalRinseA0 === 'None' ? '' : data.Wash.ThermalRinseA0}
									</Typography>
									<Typography variant="subtitle2">
										{strings[propertyName]} - {data.Wash.PumpType}
									</Typography>
								</Box>
							</Grid>
							<Grid size={{xs: 12, md: 3}} paddingTop={2}>
								<ThermometerChart
									title={strings.Temperature}
									minValue={data.Wash.Temperature.MinTempRec}
									maxValue={data.Wash.Temperature.MaxTempRec}
									lowerLimit={data.Wash.Temperature.TempSetPt1}
									upperLimit={data.Wash.Temperature.MaxCycleTemp}
									unit={data.Wash.Temperature.Unit}
								/>
							</Grid>
							{!isDescalingCycle && (
								<Grid size={{xs: 12, md: 3}} paddingTop={2}>
									<Beaker
										title={strings.Detergent}
										value={data?.Wash?.Detergent?.InjectedVol1}
										lowerLimit={data?.Wash?.Detergent?.MinInjSetPt1}
										upperLimit={data?.Wash?.Detergent?.MaxInjSetPt1}
										unit={data?.Wash?.Detergent?.Unit}
										injSet={data?.Wash?.Detergent?.InjSetPt1}
									/>
								</Grid>
							)}
							<Grid size={{xs: 12, md: 3}}  paddingTop={2} display={'flex'} flexDirection={'column'}>
								<Typography textAlign={'center'} fontWeight={'bold'} variant="body2">
									{strings.Pressure}
								</Typography>
								<Typography textAlign={'center'} variant="body1">
									{data?.Wash?.Pressure?.Unit}
								</Typography>
								<Box flex={1} display={'flex'} justifyContent={'center'} alignItems={'center'}>
									<GaugeChart
										value={data?.Wash?.Pressure?.MinPressRec}
										tickCount={21}
										startAngle={-90}
										endAngle={90}
										labelTickCount={5}
										pointerLabel={data?.Wash?.Pressure?.Unit}
										uom={data?.Wash?.Pressure?.MinPressRec}
										title={strings.Pressure}
										{...gaugeChartProps}
									/>
								</Box>
							</Grid>
							{waterLvlReachedValue && (
								<Grid size={{xs: 12, md: 3}} paddingTop={2}>
									<WaterIndicatorChart
										width="220"
										title={strings.WaterLevel}
										value={JSON.parse(waterLvlReachedValue)}
									/>
								</Grid>
							)}
							<Grid size={{xs: 12, md: 3}} paddingTop={2}>
								<Typography textAlign={'center'} fontWeight={'bold'} variant="body2">
									{strings.FillingPortPure} {data?.Wash?.Time?.WaterPort}
								</Typography>
								<DigitalGraph title={strings.SetTime} value={data?.Wash?.Time?.SetTime} />
								<DigitalGraph title={strings.RecordedTime} value={data?.Wash?.Time?.ActualTime} />
							</Grid>
						</Grid>
					</CardContent>
				</Card>
			</Grid>
		</Grid>
	);
};

export default memo(Wash);
